import { useQuery } from '@tanstack/react-query';
import {
  AnalyticsSearchEventNames
} from '@tectonic/analytics';
import { getSearchSuggestions } from '@tectonic/remix-client-network';
import {
  ElemasonWidgetActionType,
  NavigationActionType,
  type ElemasonSearchSuggestionsWidget,
  type SearchNamedQuerySource,
  type SearchSuggestionApiRouteActionResponse,
} from '@tectonic/types';
import { populate } from '@tectonic/utils';
import { useMemo } from 'react';
import { useActionDispatch } from '../../../hooks';
import { useAccessorValue } from '../../../hooks/useAccessorValue';
import { flattenSource } from '../../../utils/flattenSource';


const getDefaultSuggestion = (
  widget: ElemasonSearchSuggestionsWidget,
  searchText: string
): SearchNamedQuerySource => {
  const data = widget.data!;
  const { query } = data;

  return {
    name: populate(query.rawQuery, { searchText }),
    slug: searchText,
    source: { query: { q: searchText } },
  };
};

const queryKey = ['searchSuggestions'];

const useSearchSuggestions = (widget: ElemasonSearchSuggestionsWidget) => {
  const wData = widget.data!;
  const searchText = useAccessorValue<string>(wData.searchText);
  const actionDispatch = useActionDispatch();

  const { data: response, isLoading } =
    useQuery<SearchSuggestionApiRouteActionResponse>({
      queryKey,
      queryFn: () => getSearchSuggestions(searchText!),
      enabled: !!searchText?.trim(),
    });
  const suggestions = response?.data;

  const allSuggestions = useMemo(() => {
    const defaultSuggestion = getDefaultSuggestion(widget, searchText!);
    return [defaultSuggestion, ...(suggestions ?? [])];
  }, [suggestions, searchText, widget]);

  const onQueryClick = (query: SearchNamedQuerySource) => {
    const params = { rawQuery: query.slug, ...flattenSource(query.source) };

    // trackSearchEvent(
    //   AnalyticsSearchEventNames.SEARCH_CLICK,
    //   enrichAnalyticsPayloadWithWidgetData<AnalyticsSearchEventPayload>(
    //     {
    //       querySource: query.source,
    //     },
    //     widget
    //   )
    // );

    actionDispatch(
      {
        type: ElemasonWidgetActionType.ANALYTICS,
        payload: {
          event: AnalyticsSearchEventNames.SEARCH_CLICK,
          data: {
            querySource: query.source,
          },
        }
      }
    )

    actionDispatch({
      type: ElemasonWidgetActionType.NAVIGATE_TO,
      payload: {
        type: NavigationActionType.PATH,
        slug: 'plp',
        search: params,
      },
    });
  };

  return { suggestions: allSuggestions, isLoading, onQueryClick };
};

export { useSearchSuggestions };
